import {
  Area,
  AreaChart,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  type TooltipProps,
  XAxis,
  YAxis,
} from "recharts";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { fmtArr } from "../format";

export interface ProductTrend {
  key: string;
  name: string;
  /** One of the design-system color tokens. */
  color: "brand" | "power" | "purple" | "success" | "warning" | "danger" | "teal" | "orange";
  /** ARR series in chronological order. */
  arr: (number | null)[];
  /** Account count for each period; same length as `arr`. */
  accounts: (number | null)[];
  /** Optional contextual note ("Launched Jan 2026", "No billing Feb+"). */
  note?: string;
}

export interface ProductArrData {
  /** Period labels for the x-axis ("Jan 26", "Feb 26", …). */
  periods: string[];
  products: ProductTrend[];
}

const COLOR_HSL: Record<ProductTrend["color"], string> = {
  brand: "hsl(var(--cinch-brand))",
  power: "hsl(var(--cinch-power))",
  purple: "hsl(var(--status-purple))",
  success: "hsl(var(--status-success))",
  warning: "hsl(var(--status-warning))",
  danger: "hsl(var(--status-danger))",
  teal: "hsl(var(--status-teal))",
  orange: "hsl(var(--status-orange))",
};
const COLOR_TEXT: Record<ProductTrend["color"], string> = {
  brand: "text-brand",
  power: "text-power",
  purple: "text-purple",
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
  teal: "text-teal",
  orange: "text-orange",
};

export function ProductArrTrends({ data }: { data: ProductArrData | null }) {
  if (!data || data.products.length === 0) {
    return (
      <Card className="col-span-4 p-6">
        <div className="text-sm text-muted-foreground">
          Product breakdown pending — wire <code>ProductArrData</code> through this component.
        </div>
      </Card>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      {data.products.map((p) => (
        <ProductCard key={p.key} product={p} periods={data.periods} />
      ))}
    </div>
  );
}

function ProductCard({
  product,
  periods,
}: {
  product: ProductTrend;
  periods: string[];
}) {
  const last = product.arr.length > 0 ? product.arr[product.arr.length - 1] : null;
  const prev = product.arr.length > 1 ? product.arr[product.arr.length - 2] : null;
  const accountsLast =
    product.accounts.length > 0
      ? product.accounts[product.accounts.length - 1]
      : null;
  const delta =
    last != null && prev != null && prev > 0 ? ((last - prev) / prev) * 100 : null;
  const up = delta != null && delta >= 0;
  const color = COLOR_HSL[product.color];

  // Keep null months as null (not 0) so launch/churn gaps render as breaks in
  // the line rather than a misleading dip to zero.
  const chartData = product.arr.map((v, i) => ({
    period: periods[i] ?? String(i),
    v,
    accounts: product.accounts[i] ?? null,
  }));
  // Zoom the y-axis to the data range instead of anchoring at $0 — ARR series
  // move a few percent a month, and a zero baseline flattens that into an
  // apparent flat line. Pad so the line sits mid-plot, floor at 0.
  const values = product.arr.filter((v): v is number => v != null);
  const yDomain: [number, number] | undefined = (() => {
    if (values.length === 0) return undefined;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const pad = (max - min) * 0.35 || max * 0.05 || 1;
    return [Math.max(0, min - pad), max + pad];
  })();
  // Few months (the default 4-month preset) → label every point; long custom
  // ranges → drop the per-point labels so they never collide (axis + hover
  // tooltip still cover every month).
  const labelEveryPoint = chartData.length <= 6;

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className={cn("text-sm font-bold", COLOR_TEXT[product.color])}>
            {product.name}
          </div>
          <div
            className={cn(
              "mt-0.5 text-xl font-extrabold tracking-tight tabular-nums",
              COLOR_TEXT[product.color],
            )}
          >
            {last != null ? fmtArr(last, 1) : "—"}
          </div>
          <div className="mt-0.5 text-[10px] text-muted-foreground">
            {accountsLast ?? "—"} accounts
            {product.note && (
              <>
                {" · "}
                <em className="text-warning">{product.note}</em>
              </>
            )}
          </div>
        </div>
        {delta != null && (
          <div
            className={cn(
              "rounded-md px-2 py-0.5 text-[10px] font-bold",
              up
                ? "bg-success-light text-success"
                : "bg-danger-light text-danger",
            )}
          >
            {up ? "↑" : "↓"} {up ? "+" : ""}
            {delta.toFixed(1)}%
          </div>
        )}
      </div>

      <div className="mt-3 h-24">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 16, right: 14, bottom: 0, left: 14 }}>
            <defs>
              <linearGradient id={`pg-${product.key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="period"
              interval={labelEveryPoint ? 0 : "preserveStartEnd"}
              tickLine={false}
              axisLine={false}
              tickMargin={4}
              height={16}
              tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }}
            />
            {yDomain && <YAxis hide domain={yDomain} />}
            <Tooltip
              cursor={{ stroke: color, strokeWidth: 1, strokeDasharray: "3 3" }}
              content={(props: TooltipProps<number, string>) => {
                const row = props.active ? props.payload?.[0]?.payload : null;
                if (!row) return null;
                return (
                  <div className="rounded-md border border-border bg-popover px-2 py-1 text-[10px] shadow-md">
                    <div className="font-semibold text-popover-foreground">
                      {row.period}
                    </div>
                    <div className="font-bold tabular-nums" style={{ color }}>
                      {row.v != null ? `${fmtArr(row.v, 1)} ARR` : "No billing"}
                    </div>
                    <div className="text-muted-foreground">
                      {row.accounts != null ? `${row.accounts} accounts` : "—"}
                    </div>
                  </div>
                );
              }}
            />
            <Area
              type="monotone"
              dataKey="v"
              stroke={color}
              strokeWidth={2}
              fill={`url(#pg-${product.key})`}
              connectNulls={false}
              dot={{ r: 2, fill: color, strokeWidth: 0 }}
              activeDot={{ r: 3.5 }}
              isAnimationActive={false}
            >
              {labelEveryPoint && (
                <LabelList
                  dataKey="v"
                  position="top"
                  offset={6}
                  className="tabular-nums"
                  fill={color}
                  fontSize={9}
                  fontWeight={700}
                  formatter={(v: unknown) => (v == null ? "" : fmtArr(Number(v), 1))}
                />
              )}
            </Area>
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-2 flex items-center gap-1.5 text-[9px] text-muted-foreground">
        <span className="h-0.5 w-3.5 rounded-full" style={{ background: color }} />
        <span className="font-semibold">Annualized ARR</span>
        <span className="text-muted-foreground/50">· MRR × 12</span>
        {!labelEveryPoint && (
          <span className="ml-auto text-muted-foreground/50">
            hover for monthly $
          </span>
        )}
      </div>
    </Card>
  );
}
